// Le planning des salles est un MODULE (D-122, D-123) : tous ses points d'entrée sont ici, pour le brancher sur la table
// des modules du club en quelques lignes à la fusion.
//   * sallesOn(ctx)              : l'interrupteur (vrai tant que la table des modules n'est pas là) ; à la fusion :
//                                  `moduleOn(ctx, 'salles')`. Éteint : pas de tuile, pas de route, le « Lieu » en texte libre
//                                  dans la fiche d'une séance (salles-form.ts) ;
//   * SALLES                     : la clé du module, sa route, la sorte de ses notifications (« hall », liens « #/salles… »),
//                                  ses tutos, ses visites, son écran d'aide (pour filtrer l'aide et le guide) ;
//   * sallesBureauTile / sallesMemberTiles : les tuiles de l'onglet Club (club-hub.ts) ;
//   * renderSallesRoute          : la route #/salles (app.ts).
// Côté base : _salles_on(club) (migration 20261102700000_salles.sql) est le même interrupteur.
import { clubHalls, hallBoard, hallRequests, hallsBusy, planCheck, plagesView } from '../lib/salles-api';
import { addDays, clashCount, freedFor, withArticle, ymd } from '../domain/salles';
import { todos } from '../domain/creneaux';
import { MODULES } from '../domain/modules';
import { isMinorAccount, modOn, type Ctx } from './context';
import { html, mount, type Html } from './dom';
import { myClubFunctions } from './recus-entries';
import { openAskSheet, renderSalles } from './salles';

export const SALLES = {
  module: 'salles',
  label: 'Planning des salles',
  route: 'salles',
  notifKind: 'hall',
  articles: ['planning-salle', 'demander-salle', 'salles-club', 'repondre-salle', 'nos-creneaux', 'creneau-libre', 'gestionnaires-salles',
    'vacances-salles', 'souhaits-salles', 'arbitrer-salles', 'creneau-perdu', 'seance-sans-salle', 'programmer-creneau', 'mise-en-coherence', 'zone-vacances',
    'planning-attribue', 'matchs-plages'],
  tours: ['planning-salle', 'demander-salle', 'nos-creneaux', 'creneau-libre', 'vacances-3-etapes', 'zone-vacances', 'vacances-salles', 'souhaits-salles', 'arbitrer-salles', 'creneau-perdu',
    'programmer-creneau', 'mise-en-coherence', 'planning-attribue', 'matchs-plages'],
  screens: ['salles', 'salles/vacances', 'salles/jour', 'salles/reglages'],
} as const;

/** Le module est-il activé pour le club affiché ? (D-122 : la table des modules du club.) */
export const sallesOn = (ctx: Ctx): boolean => !!ctx.club && modOn(ctx, 'salles');

export const renderSallesRoute = (view: HTMLElement, ctx: Ctx, rest: string[]) => renderSalles(view, ctx, rest);

const tile = (href: string, icon: string, title: string, sub: string, hot = false, extra: Html | '' = ''): Html => html`
  <a class="hub-tile ${hot ? 'hot' : ''}" href="${href}" ${extra}>
    <span class="ti ${icon}" aria-hidden="true"></span><strong>${title}</strong><small>${sub}</small></a>`;

/** Ce qui est à faire dans Les salles (D-130, D-138) : souhaits, demandes de créneau, créneaux perdus, réceptions ; D-144 :
 *  les matchs à domicile hors de notre planning attribué (une ligne), les lieux FFBB à relier. */
async function toTreat(clubId: string): Promise<{ n: number; role: string | null; collecting: string | null }> {
  const t = ymd(new Date());
  const [b, reqs] = await Promise.all([hallBoard(clubId, t, addDays(t, 6)).catch(() => null), hallRequests(clubId).catch(() => null)]);
  if (!b) return { n: 0, role: null, collecting: null };
  // D-135 : la mise en cohérence (les séances qui ne collent à aucun créneau) compte pour une chose à traiter.
  const coh = b.manage ? ((await planCheck(clubId).catch(() => null))?.items.length ?? 0) > 0 ? 1 : 0 : 0;
  const pv = b.manage ? await plagesView(clubId, t, t).catch(() => null) : null;
  const rep = pv?.reprise?.state === 'to_check' && pv.reprise.season === pv.season;
  const planned = !!pv?.plages.some(p => p.season === pv.season);
  const plage = pv ? (planned && !rep && pv.out.some(m => !m.derog && !m.lost) ? 1 : 0) + (rep ? 1 : pv.venues.length) : 0;
  const bookings = (b.manage ? (reqs?.list ?? []).filter(r => r.status === 'pending').length : 0) + coh + plage;
  const open = b.periods.find(p => p.status === 'open' && p.mode === 'apart' && (!p.wishes_until || p.wishes_until >= b.today) && p.back > b.today
    && (!(p as { prefill?: boolean }).prefill || !!(p as { sent_at?: string | null }).sent_at));
  // D-138 : les vacances en 3 étapes ont leur bandeau (pas une ligne de l'à-faire).
  const n = todos({ ...b, periods: b.periods.filter(p => !(p as { prefill?: boolean }).prefill) }).length;
  return { n: n + bookings, role: b.role, collecting: open ? open.label : null };
}

/** Bureau, rubrique Sportif : « Les salles » (D-138 ; D-130 : ce qui est à faire, un chevauchement). */
export async function sallesBureauTile(ctx: Ctx, clubId: string): Promise<Html | ''> {
  if (!sallesOn(ctx)) return '';
  const t = ymd(new Date());
  const [busy, tr] = await Promise.all([hallsBusy(clubId, t, addDays(t, 13)).catch(() => null), toTreat(clubId)]);
  const clashes = busy ? busy.halls.reduce((n, h) => n + clashCount(busy, h.id), 0) : 0;
  const sub = [tr.n ? `${tr.n} à faire` : '', clashes ? `${clashes} chevauchement${clashes > 1 ? 's' : ''}` : ''].filter(Boolean).join(' · ');
  // L'icône du module (D-132 : une icône par module), pas celle des équipes (B12).
  return tile('#/salles', MODULES.salles.icon, 'Les salles', sub || 'La semaine, les créneaux, les vacances', tr.n + clashes > 0, html`data-guide="hub-salles"`);
}

/** L'accueil du responsable des salles hors bureau (K3, D-130) : « Les salles · N à faire » en tête, comme l'« À faire »
 *  du bureau. Seulement pour qui tient une fonction (la liste est gardée une minute) et quand il y a quelque chose. */
export async function renderSallesRespCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club || !sallesOn(ctx) || isMinorAccount(ctx)) return;
  if (!(await myClubFunctions(ctx)).length) return;
  const tr = await toTreat(ctx.club.id).catch(() => null);
  if (!tr || tr.role !== 'resp' || !tr.n) return;
  mount(el, html`<a class="card card-row" href="#/salles" data-guide="home-salles-resp">
    <span class="mi-ico" aria-hidden="true"><span class="ti ${MODULES.salles.icon}"></span></span>
    <span class="card-main"><span class="eyebrow">Responsable des salles</span><strong>Les salles · ${tr.n} à faire</strong>
      <span class="muted small">Souhaits, demandes de créneau, créneaux perdus, réceptions${tr.collecting ? ` · ${tr.collecting} : souhaits en cours` : ''}</span></span>
    <span class="chip chip-accent">${tr.n}</span></a>`);
}

/** Membres : « Planning des salles » (un coach : un créneau libéré à prendre), « Demander le club-house » (un adulte). */
export async function sallesMemberTiles(ctx: Ctx, clubId: string): Promise<(Html | '')[]> {
  if (!sallesOn(ctx) || isMinorAccount(ctx)) return [];
  const coach = ctx.teams.some(t => t.club_id === clubId && ctx.staffOf.has(t.id));
  const t = ymd(new Date());
  const [busy, tr] = await Promise.all([coach ? hallsBusy(clubId, t, addDays(t, 6)).catch(() => null) : Promise.resolve(null), toTreat(clubId)]);
  const reception = (await clubHalls(clubId).catch(() => [])).filter(h => h.kind === 'reception');
  const freed = busy ? freedFor(busy, new Set(ctx.teams.filter(x => ctx.staffOf.has(x.id)).map(x => x.id)), new Date()) : 0;
  const first = reception.find(h => h.owner === 'club') ?? reception[0];
  current = { ctx, clubId };
  // Le responsable des salles (D-130), même hors bureau : Les salles, ce qui est à faire.
  const resp = tr.role === 'resp';
  return [
    resp ? tile('#/salles', MODULES.salles.icon, 'Les salles', tr.n ? `${tr.n} à faire` : 'La semaine, les créneaux, les vacances', tr.n > 0, html`data-guide="hub-salles"`) : '',
    !resp && coach && busy ? tile('#/salles', MODULES.salles.icon, 'Les salles', tr.collecting ? `${tr.collecting} : tes créneaux à confirmer` : freed ? `${freed} créneau${freed > 1 ? 'x' : ''} libéré${freed > 1 ? 's' : ''} cette semaine` : 'Qui occupe quel créneau', !!tr.collecting || freed > 0,
      html`data-guide="hub-salles-coach"`) : '',
    first ? tile('#/salles/demander', MODULES.salles.icon, `Demander ${withArticle(first.name)}`, 'Un pot, une réunion, un anniversaire', false,
      html`data-guide="sal-ask-tile" data-salle-ask="${first.id}"`) : '',
  ];
}

// « Demander le club-house » depuis l'onglet Club : la demande s'ouvre sur place (maquette s4).
let current: { ctx: Ctx; clubId: string } | null = null;
document.addEventListener('click', e => {
  const a = (e.target as HTMLElement).closest<HTMLElement>('[data-salle-ask]');
  if (!a || !current || a.closest('.gd-layer')) return;
  e.preventDefault();
  void openAskSheet(current.ctx, current.clubId, a.dataset.salleAsk ?? null);
});
